import type React from 'react';
import {
    Globe, Megaphone, Facebook, Instagram, Chrome, MessageCircle, Phone, User,
} from 'lucide-react';

export type LeadSource =
    | 'sito'
    | 'meta'
    | 'facebook'
    | 'instagram'
    | 'google'
    | 'whatsapp'
    | 'telefono'
    | 'personale';

export type LeadStage =
    | 'nuovo'
    | 'contattato'
    | 'qualificato'
    | 'proposta'
    | 'vinto'
    | 'perso';

export type LeadEventType =
    | 'call'
    | 'email'
    | 'note'
    | 'meeting'
    | 'whatsapp'
    | 'stage_change'
    | 'conversione';

export type LeadEventStatus = 'open' | 'done' | 'cancelled';

// Etichetta colore scelta a mano sulla card del lead (colonna leads.color).
export type LeadColor = 'rosso' | 'arancione' | 'giallo' | 'verde' | 'blu' | 'viola';

export interface Lead {
    id: string;
    source: LeadSource;
    external_lead_id: string | null;
    raw: Record<string, unknown> | null;
    campaign_id: string | null;
    campaign_name: string | null;
    form_id: string | null;
    form_name: string | null;
    ad_id: string | null;
    stage: LeadStage;
    full_name: string | null;
    company_name: string | null;
    email: string | null;
    phone: string | null;
    city: string | null;
    province: string | null;
    notes: string | null;
    next_action: string | null; // prossima azione da fare (cosa)
    next_action_at: string | null; // quando va fatta
    lost_reason: string | null; // motivo se stage = 'perso'
    color: LeadColor | null; // etichetta colore manuale (bordo card)
    assigned_to: string | null;
    converted_customer_id: string | null;
    stage_changed_at: string | null;
    last_event_at: string | null;
    created_at: string;
    updated_at: string;
    created_by: string | null;
    // Joins
    assignee?: { id: string; full_name: string | null; email: string } | null;
    reminders?: LeadReminder[];
}

export interface LeadEvent {
    id: string;
    lead_id: string;
    event_type: LeadEventType;
    title: string;
    notes: string | null;
    event_date: string;
    status: LeadEventStatus;
    created_at: string;
    updated_at: string;
    created_by: string | null;
    // Joins
    reminders?: LeadReminder[];
    creator?: EventCreator | null;
}

/** Profilo (ridotto) dell'utente che ha creato l'evento in timeline. */
export interface EventCreator {
    id: string;
    full_name: string | null;
    email: string;
}

export interface LeadReminder {
    id: string;
    event_id: string;
    remind_at: string;
    is_completed: boolean;
    created_at: string;
    created_by: string | null;
}

// Reminder "due" arricchito con i dati del lead per la notifica in-app
export interface DueLeadReminder extends LeadReminder {
    event: {
        lead_id: string;
        title: string;
        lead: { full_name: string | null; company_name: string | null } | null;
    } | null;
}

export interface LeadStageMeta {
    key: LeadStage;
    label: string;
    // classi tailwind per header colonna kanban e badge
    headerClass: string;
    badgeClass: string;
    dotClass: string;
}

// Ordine e stile delle colonne della pipeline
export const LEAD_STAGES: LeadStageMeta[] = [
    { key: 'nuovo', label: 'Nuovo', headerClass: 'bg-blue-50 text-blue-700 border-blue-200', badgeClass: 'bg-blue-100 text-blue-700', dotClass: 'bg-blue-500' },
    { key: 'contattato', label: 'Contattato', headerClass: 'bg-indigo-50 text-indigo-700 border-indigo-200', badgeClass: 'bg-indigo-100 text-indigo-700', dotClass: 'bg-indigo-500' },
    { key: 'qualificato', label: 'Qualificato', headerClass: 'bg-purple-50 text-purple-700 border-purple-200', badgeClass: 'bg-purple-100 text-purple-700', dotClass: 'bg-purple-500' },
    { key: 'proposta', label: 'Proposta', headerClass: 'bg-amber-50 text-amber-700 border-amber-200', badgeClass: 'bg-amber-100 text-amber-700', dotClass: 'bg-amber-500' },
    { key: 'vinto', label: 'Vinto', headerClass: 'bg-green-50 text-green-700 border-green-200', badgeClass: 'bg-green-100 text-green-700', dotClass: 'bg-green-500' },
    { key: 'perso', label: 'Perso', headerClass: 'bg-red-50 text-red-700 border-red-200', badgeClass: 'bg-red-100 text-red-700', dotClass: 'bg-red-500' },
];

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = LEAD_STAGES.reduce(
    (acc, s) => ({ ...acc, [s.key]: s.label }),
    {} as Record<LeadStage, string>
);

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
    sito: 'Sito web',
    meta: 'Meta',
    facebook: 'Facebook',
    instagram: 'Instagram',
    google: 'Google Ads',
    whatsapp: 'WhatsApp',
    telefono: 'Telefono',
    personale: 'Personale',
};

// Icona (lucide) e colore per ogni provenienza — usati da card, dettaglio, filtri.
export const LEAD_SOURCE_ICONS: Record<LeadSource, React.ElementType> = {
    sito: Globe,
    meta: Megaphone,
    facebook: Facebook,
    instagram: Instagram,
    google: Chrome,
    whatsapp: MessageCircle,
    telefono: Phone,
    personale: User,
};

export const LEAD_SOURCE_COLORS: Record<LeadSource, string> = {
    sito: 'text-sky-600',
    meta: 'text-blue-600',
    facebook: 'text-blue-700',
    instagram: 'text-pink-600',
    google: 'text-red-500',
    whatsapp: 'text-emerald-600',
    telefono: 'text-green-600',
    personale: 'text-gray-500',
};

// Ordine per select/filtri
export const LEAD_SOURCE_KEYS: LeadSource[] = [
    'sito', 'meta', 'facebook', 'instagram', 'google', 'whatsapp', 'telefono', 'personale',
];

// --- Colori card -------------------------------------------------------------
export interface LeadColorMeta {
    key: LeadColor;
    label: string;
    dotClass: string; // pallino pieno nel selettore
    borderClass: string; // bordo della card
}

// Classi tailwind statiche (niente stringhe dinamiche: la build le deve vedere).
export const LEAD_COLORS: LeadColorMeta[] = [
    { key: 'rosso', label: 'Rosso', dotClass: 'bg-red-500', borderClass: 'border-red-500' },
    { key: 'arancione', label: 'Arancione', dotClass: 'bg-orange-500', borderClass: 'border-orange-500' },
    { key: 'giallo', label: 'Giallo', dotClass: 'bg-yellow-400', borderClass: 'border-yellow-400' },
    { key: 'verde', label: 'Verde', dotClass: 'bg-emerald-500', borderClass: 'border-emerald-500' },
    { key: 'blu', label: 'Blu', dotClass: 'bg-blue-500', borderClass: 'border-blue-500' },
    { key: 'viola', label: 'Viola', dotClass: 'bg-violet-500', borderClass: 'border-violet-500' },
];

export const LEAD_COLOR_META: Record<LeadColor, LeadColorMeta> = LEAD_COLORS.reduce(
    (acc, c) => ({ ...acc, [c.key]: c }),
    {} as Record<LeadColor, LeadColorMeta>
);

// --- Staleness / "da ricontattare" ------------------------------------------
// Stadi "attivi" in cui un lead è in lavorazione e va ricontattato se resta fermo.
// Escludono 'nuovo' (ha già l'alert nuovo-lead) e 'vinto'/'perso' (chiusi).
export const LEAD_ACTIVE_STAGES: LeadStage[] = ['contattato', 'qualificato', 'proposta'];

// Giorni oltre i quali un lead attivo è "da ricontattare". Cambiala qui per tarare.
export const LEAD_RECONTACT_DAYS = 3;

export type LeadStaleLevel = 'fresh' | 'warn' | 'stale';

export interface LeadStaleness {
    days: number; // giorni interi dall'ultimo contatto
    level: LeadStaleLevel; // fresh < soglia · warn >= soglia · stale >= 2x soglia
    applicable: boolean; // true solo per gli stadi attivi
}

// Ultimo "tocco" del lead: ultimo evento, poi cambio stadio, poi creazione.
// last_event_at è ricalcolato dal trigger DB a ogni evento => reset automatico.
export function leadLastTouch(lead: Lead): Date {
    return new Date(lead.last_event_at || lead.stage_changed_at || lead.created_at);
}

export function leadStaleness(lead: Lead, thresholdDays = LEAD_RECONTACT_DAYS): LeadStaleness {
    const applicable = LEAD_ACTIVE_STAGES.includes(lead.stage);
    const ms = Date.now() - leadLastTouch(lead).getTime();
    const days = Math.max(0, Math.floor(ms / 86_400_000));
    let level: LeadStaleLevel = 'fresh';
    if (applicable) {
        if (days >= thresholdDays * 2) level = 'stale';
        else if (days >= thresholdDays) level = 'warn';
    }
    return { days, level, applicable };
}

// --- Motivo "perso" ----------------------------------------------------------
export interface LeadLostReason {
    key: string;
    label: string;
}

// Motivi selezionabili quando un lead passa a 'perso'. Salvati come TEXT (key).
export const LEAD_LOST_REASONS: LeadLostReason[] = [
    { key: 'no_risposta', label: 'Non risponde / irreperibile' },
    { key: 'prezzo', label: 'Prezzo troppo alto' },
    { key: 'concorrente', label: 'Scelto un concorrente' },
    { key: 'non_interessato', label: 'Non interessato' },
    { key: 'tempistiche', label: 'Tempistiche non compatibili' },
    { key: 'budget', label: 'Budget insufficiente' },
    { key: 'non_qualificato', label: 'Non in target' },
    { key: 'altro', label: 'Altro' },
];

export const LEAD_LOST_REASON_LABELS: Record<string, string> = LEAD_LOST_REASONS.reduce(
    (acc, r) => ({ ...acc, [r.key]: r.label }),
    {} as Record<string, string>
);

// --- Prossima azione ---------------------------------------------------------
// true se la prossima azione ha una data ed è già scaduta (o entro `withinMs`).
export function leadNextActionDue(lead: Lead, withinMs = 0): boolean {
    if (!lead.next_action_at) return false;
    return new Date(lead.next_action_at).getTime() <= Date.now() + withinMs;
}

export const LEAD_EVENT_TYPE_LABELS: Record<LeadEventType, string> = {
    call: 'Chiamata',
    email: 'Email',
    note: 'Nota',
    meeting: 'Meeting',
    whatsapp: 'Messaggio WhatsApp',
    stage_change: 'Cambio stato',
    conversione: 'Conversione',
};
